/**
 * Pricing and VAT (#2465, split out of the 991-line workshop form).
 *
 * The hourly rate and the additional-work limit are held as strings while
 * typing and committed on blur, so this section owns that input state. The
 * rate is entered gross and the API recomputes the net one, which is why the
 * sync effect watches both stored values.
 *
 * VAT rates live here rather than with billing because they are an input to
 * price calculation, not an invoice identifier.
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Grid,
  InputAdornment,
  Button,
  IconButton,
  Chip,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import type { WorkshopSettings, VatRate } from '@/types';
import { eurosToCents } from '@/utils/money';
import { DEFAULT_VAT_PERCENT, netToGrossCents } from '@/utils/vat';

interface Props {
  settings: WorkshopSettings;
  onChange: (settings: WorkshopSettings) => void;
}

export function WorkshopSectionPricing({ settings, onChange }: Props) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');

  const rateIsZero = (settings.laborRateGrossCents ?? settings.laborRateCents ?? 0) <= 0;

  const displayLaborRateCents = settings.laborRateGrossCents != null && settings.laborRateGrossCents > 0
    ? settings.laborRateGrossCents
    : netToGrossCents(settings.laborRateCents ?? 0, DEFAULT_VAT_PERCENT);
  const [laborRateInput, setLaborRateInput] = useState(
    displayLaborRateCents > 0 ? (displayLaborRateCents / 100).toFixed(2) : '',
  );

  // Workshop-wide default (€) for the pre-approved additional-work limit (#296).
  const [additionalWorkLimitInput, setAdditionalWorkLimitInput] = useState(
    settings.defaultAdditionalWorkLimitCents != null
      ? (settings.defaultAdditionalWorkLimitCents / 100).toFixed(2).replace('.', ',')
      : '',
  );

  useEffect(() => {
    const next = settings.laborRateGrossCents != null && settings.laborRateGrossCents > 0
      ? settings.laborRateGrossCents
      : netToGrossCents(settings.laborRateCents ?? 0, DEFAULT_VAT_PERCENT);
    setLaborRateInput(next > 0 ? (next / 100).toFixed(2) : '');
  }, [settings.laborRateCents, settings.laborRateGrossCents]);

  useEffect(() => {
    setAdditionalWorkLimitInput(
      settings.defaultAdditionalWorkLimitCents != null
        ? (settings.defaultAdditionalWorkLimitCents / 100).toFixed(2).replace('.', ',')
        : '',
    );
  }, [settings.defaultAdditionalWorkLimitCents]);

  return (
    <>
      <Typography variant="h6" fontWeight={600} gutterBottom>
        {t('pricing')}
      </Typography>

      <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 4 }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            {/*
              Required and warned about, matching the first-run wizard
              (WorkshopStep.tsx). A zero rate is the one gap no error ever
              reports: the work is simply invoiced at nothing (#2469).
            */}
            <TextField
              fullWidth
              required
              error={rateIsZero}
              label={t('laborRateInclVat')}
              type="number"
              value={laborRateInput}
              onChange={(e) => setLaborRateInput(e.target.value)}
              onBlur={() => {
                const cents = eurosToCents(laborRateInput.replace(',', '.')) ?? 0;
                onChange({ ...settings, laborRateGrossCents: cents });
                setLaborRateInput((cents / 100).toFixed(2));
              }}
              helperText={rateIsZero ? t('readiness.issue.laborRate') : undefined}
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                  endAdornment: <InputAdornment position="end">/ {t('hour')}</InputAdornment>,
                },
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label={t('defaultAdditionalWorkLimit')}
              helperText={t('defaultAdditionalWorkLimitHint')}
              value={additionalWorkLimitInput}
              onChange={(e) => setAdditionalWorkLimitInput(e.target.value)}
              onBlur={() => {
                const trimmed = additionalWorkLimitInput.trim();
                if (trimmed === '') {
                  onChange({ ...settings, defaultAdditionalWorkLimitCents: undefined });
                  setAdditionalWorkLimitInput('');
                  return;
                }
                const cents = eurosToCents(trimmed);
                if (cents == null || cents < 0) {
                  setAdditionalWorkLimitInput(
                    settings.defaultAdditionalWorkLimitCents != null
                      ? (settings.defaultAdditionalWorkLimitCents / 100).toFixed(2).replace('.', ',')
                      : '',
                  );
                  return;
                }
                onChange({ ...settings, defaultAdditionalWorkLimitCents: cents });
                setAdditionalWorkLimitInput((cents / 100).toFixed(2).replace('.', ','));
              }}
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                },
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* VAT Rates */}
      <Typography variant="h6" fontWeight={600} gutterBottom>
        {t('vatRates.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('vatRates.subtitle')}
      </Typography>

      <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 4 }}>
        {(settings.vatRates ?? []).map((rate) => (
          <Box
            key={rate.id}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              py: 1,
              '&:hover': { bgcolor: 'action.hover', borderRadius: 1 },
            }}
          >
            <TextField
              size="small"
              sx={{ flex: 1 }}
              label={t('vatRates.label')}
              value={rate.label}
              onChange={(e) => {
                const updated = settings.vatRates.map((r) =>
                  r.id === rate.id ? { ...r, label: e.target.value } : r
                );
                onChange({ ...settings, vatRates: updated });
              }}
            />
            <TextField
              size="small"
              type="number"
              sx={{ width: 110 }}
              label={t('vatRates.percentage')}
              value={rate.percentage}
              onChange={(e) => {
                const updated = settings.vatRates.map((r) =>
                  r.id === rate.id ? { ...r, percentage: parseFloat(e.target.value) || 0 } : r
                );
                onChange({ ...settings, vatRates: updated });
              }}
              slotProps={{
                input: {
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                },
                htmlInput: { min: 0, max: 100, step: '0.1' },
              }}
            />
            {settings.defaultVatRateId === rate.id ? (
              <Chip
                label={t('vatRates.default')}
                size="small"
                color="primary"
                onDelete={() => onChange({ ...settings, defaultVatRateId: null })}
              />
            ) : (
              <Tooltip title={t('vatRates.setDefault')}>
                <Chip
                  label={t('vatRates.default')}
                  size="small"
                  variant="outlined"
                  onClick={() => onChange({ ...settings, defaultVatRateId: rate.id })}
                />
              </Tooltip>
            )}
            <Tooltip title={tc('delete')}>
              <IconButton
                size="small"
                onClick={() => {
                  const updated = settings.vatRates.filter((r) => r.id !== rate.id);
                  const newDefault = settings.defaultVatRateId === rate.id ? null : settings.defaultVatRateId;
                  onChange({ ...settings, vatRates: updated, defaultVatRateId: newDefault });
                }}
                aria-label={tc('delete')}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ))}
        {(settings.vatRates ?? []).length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1, textAlign: 'center' }}>
            —
          </Typography>
        )}
        <Button
          size="small"
          startIcon={<AddIcon />}
          sx={{ mt: 1 }}
          onClick={() => {
            const newRate: VatRate = {
              id: crypto.randomUUID(),
              label: '',
              percentage: 25.5,
            };
            onChange({ ...settings, vatRates: [...(settings.vatRates ?? []), newRate] });
          }}
        >
          {t('vatRates.add')}
        </Button>
      </Paper>
    </>
  );
}
